import React, { useEffect, useMemo, useRef, useState } from "react";
import { database } from "../../firebaseConfig";
import { ref, get, push, set } from "firebase/database";
import DocumentNumberField from "./DocumentNumberField";
import { PAYMENT_TYPES } from "./OfferLanguage";
import {
  analyzeDocumentNumber,
  buildNumberIndex,
  collectDocumentNumbers,
  suggestNextNumber,
} from "./utils/documentNumbers";
import {
  PAYMENT_TERM_PRESETS,
  buildReissuedInvoice,
  daysBetweenDateInputs,
  derivePaymentTermDays,
  resolveOfferLines,
  shiftDateInput,
  toDateInputValue,
} from "./utils/invoiceReissue";
import {
  formatCurrency,
  parsePriceInput,
  toPriceInputValue,
} from "./utils/priceInput";
import "./ReissueInvoiceModal.css";

const PAYMENT_LABELS = {
  [PAYMENT_TYPES.RECURRING]: { label: "Monthly", icon: "fa-rotate" },
  [PAYMENT_TYPES.PER_APPOINTMENT]: {
    label: "Per appt.",
    icon: "fa-calendar-check",
  },
};

/**
 * Creates the next invoice for a recipient that is already billed.
 *
 * The recurring positions of a retainer stay identical month after month while
 * the commission based ones change, so this asks for the amounts and nothing
 * else: numbering, dates and totals are derived, everything else is inherited
 * from the document it was started from.
 */
const ReissueInvoiceModal = ({ userId, sourceOffer, onClose, onCreated }) => {
  const [catalog, setCatalog] = useState(null);
  const [documents, setDocuments] = useState({});
  const [prices, setPrices] = useState({});
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(() =>
    toDateInputValue(Date.now())
  );
  const [termDays, setTermDays] = useState(() =>
    derivePaymentTermDays(sourceOffer)
  );
  const [dueDate, setDueDate] = useState("");
  const [customerName, setCustomerName] = useState(
    sourceOffer?.customerName || ""
  );
  const [customerAddress, setCustomerAddress] = useState(
    sourceOffer?.customerAddress || ""
  );
  const [isEditingRecipient, setIsEditingRecipient] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const [arePricesPrefilled, setArePricesPrefilled] = useState(false);

  const numberPrefilledRef = useRef(false);
  const focusRef = useRef(null);

  const sourceTermDays = useMemo(
    () => derivePaymentTermDays(sourceOffer),
    [sourceOffer]
  );
  const sourceWasInvoice = sourceOffer?.documentType === "invoice";

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;

    const load = async () => {
      const [servicesSnapshot, offersSnapshot] = await Promise.all([
        get(ref(database, `/World/${userId}/services`)),
        get(ref(database, `/World/${userId}/offers`)),
      ]);
      if (cancelled) return;

      const servicesData = servicesSnapshot.exists() ? servicesSnapshot.val() : {};
      setCatalog(
        Object.entries(servicesData).map(([id, data]) => ({ id, ...data }))
      );
      setDocuments(offersSnapshot.exists() ? offersSnapshot.val() : {});
    };

    load().catch((loadError) => {
      console.error("Error preparing invoice reissue:", loadError);
      if (cancelled) return;
      setCatalog([]);
      setError("The services of this document could not be loaded.");
    });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isSaving) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isSaving, onClose]);

  const numberIndex = useMemo(
    () => buildNumberIndex(collectDocumentNumbers(documents, "invoice")),
    [documents]
  );

  const numberAnalysis = useMemo(
    () =>
      analyzeDocumentNumber(invoiceNumber, numberIndex, {
        noun: "invoice number",
      }),
    [invoiceNumber, numberIndex]
  );

  useEffect(() => {
    if (numberPrefilledRef.current) return;
    const suggestion = suggestNextNumber(numberIndex);
    if (!suggestion) return;
    numberPrefilledRef.current = true;
    setInvoiceNumber(suggestion);
  }, [numberIndex]);

  const lines = useMemo(
    () => (catalog ? resolveOfferLines(sourceOffer, catalog) : []),
    [catalog, sourceOffer]
  );

  useEffect(() => {
    if (arePricesPrefilled || !catalog) return;
    setPrices(
      Object.fromEntries(
        lines.map((line) => [line.id, toPriceInputValue(line.price)])
      )
    );
    setArePricesPrefilled(true);
  }, [arePricesPrefilled, catalog, lines]);

  // The variable position is what someone opened this dialog for, so that is
  // where the cursor starts.
  const focusTargetId = useMemo(() => {
    const billable = lines.filter((line) => !line.isMissing);
    if (!billable.length) return null;
    const variable = billable.find(
      (line) => line.paymentType !== PAYMENT_TYPES.RECURRING
    );
    return (variable || billable[0]).id;
  }, [lines]);

  // Waiting for the prefill keeps the selection on the amount that is already
  // in the field, so the first keystroke replaces it instead of appending.
  useEffect(() => {
    if (!arePricesPrefilled || !focusTargetId || !focusRef.current) return;
    focusRef.current.focus();
    focusRef.current.select();
  }, [arePricesPrefilled, focusTargetId]);

  // A term of its own means the due date was picked by hand and stays put.
  useEffect(() => {
    if (termDays === null) return;
    setDueDate(shiftDateInput(invoiceDate, termDays));
  }, [invoiceDate, termDays]);

  const parsedPrices = useMemo(
    () =>
      lines.reduce((collected, line) => {
        const parsed = parsePriceInput(prices[line.id]);
        collected[line.id] = parsed === null ? line.price : parsed;
        return collected;
      }, {}),
    [lines, prices]
  );

  const draft = useMemo(
    () =>
      catalog
        ? buildReissuedInvoice({
            source: sourceOffer,
            lines,
            prices: parsedPrices,
            invoiceNumber,
            invoiceDate,
            dueDate,
            customerName,
            customerAddress,
          })
        : null,
    [
      catalog,
      sourceOffer,
      lines,
      parsedPrices,
      invoiceNumber,
      invoiceDate,
      dueDate,
      customerName,
      customerAddress,
    ]
  );

  const effectiveTermDays =
    termDays === null ? daysBetweenDateInputs(invoiceDate, dueDate) : termDays;
  const billableLines = lines.filter((line) => !line.isMissing);
  const isLoading = catalog === null;
  const canSubmit =
    !isLoading && !isSaving && billableLines.length > 0 && !!invoiceNumber.trim();

  const handleDueDateChange = (value) => {
    setTermDays(null);
    setDueDate(value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || !draft) return;

    setIsSaving(true);
    setError("");

    try {
      // The dialog may have been open for a while, so the number is checked
      // against live data instead of the snapshot behind the inline hint.
      const snapshot = await get(ref(database, `/World/${userId}/offers`));
      const liveAnalysis = analyzeDocumentNumber(
        invoiceNumber,
        buildNumberIndex(
          collectDocumentNumbers(snapshot.exists() ? snapshot.val() : {}, "invoice")
        ),
        { noun: "invoice number" }
      );

      if (liveAnalysis.status === "duplicate") {
        setError(
          liveAnalysis.suggestion
            ? `${liveAnalysis.message} Next free number: ${liveAnalysis.suggestion}`
            : liveAnalysis.message
        );
        setIsSaving(false);
        return;
      }

      const newInvoiceRef = push(ref(database, `/World/${userId}/offers`));
      await set(newInvoiceRef, draft);
      onCreated({ id: newInvoiceRef.key, invoice: draft });
    } catch (saveError) {
      console.error("Error creating reissued invoice:", saveError);
      setError("The invoice could not be created. Please try again.");
      setIsSaving(false);
    }
  };

  const renderLine = (line) => {
    const draftValue = prices[line.id] ?? "";
    const parsed = parsePriceInput(draftValue);
    const nextPrice = parsed === null ? line.price : parsed;
    const hasChanged = !line.isMissing && nextPrice !== line.price;
    const payment = PAYMENT_LABELS[line.paymentType];

    return (
      <li
        key={line.id}
        className={`reissue-line${line.isMissing ? " reissue-line--missing" : ""}${
          hasChanged ? " reissue-line--changed" : ""
        }`}
      >
        <div className="reissue-line__text">
          <span className="reissue-line__title">{line.title}</span>
          <div className="reissue-line__meta">
            {payment && (
              <span className="reissue-line__tag reissue-line__tag--accent">
                <i className={`fas ${payment.icon}`} aria-hidden="true"></i>
                {payment.label}
              </span>
            )}
            {line.discountPercent > 0 && (
              <span className="reissue-line__tag reissue-line__tag--discount">
                −{line.discountPercent}%
              </span>
            )}
            {hasChanged && (
              <span className="reissue-line__tag">
                before {formatCurrency(line.price)}
              </span>
            )}
            {line.isMissing && (
              <span className="reissue-line__tag reissue-line__tag--warning">
                <i className="fas fa-circle-exclamation" aria-hidden="true"></i>
                No longer in your service library
              </span>
            )}
            {!line.isMissing && line.discountPercent > 0 && (
              <span className="reissue-line__tag">
                {formatCurrency(nextPrice * (1 - line.discountPercent / 100))}{" "}
                after discount
              </span>
            )}
          </div>
        </div>

        {line.isMissing ? (
          <span className="reissue-line__void">not billed</span>
        ) : (
          <div className="reissue-line__price">
            <input
              ref={line.id === focusTargetId ? focusRef : undefined}
              type="text"
              inputMode="decimal"
              className="reissue-line__input"
              value={draftValue}
              aria-label={`Price for ${line.title}`}
              onChange={(event) =>
                setPrices((previous) => ({
                  ...previous,
                  [line.id]: event.target.value,
                }))
              }
              onBlur={() =>
                setPrices((previous) => ({
                  ...previous,
                  [line.id]: toPriceInputValue(nextPrice),
                }))
              }
            />
            <span className="reissue-line__currency">€</span>
          </div>
        )}
      </li>
    );
  };

  return (
    <div className="reissue-modal__overlay" role="presentation">
      <form
        className="reissue-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reissue-modal-title"
        onSubmit={handleSubmit}
      >
        <header className="reissue-modal__header">
          <div className="reissue-modal__heading">
            <h3 className="reissue-modal__title" id="reissue-modal-title">
              <i className="fas fa-file-invoice-dollar" aria-hidden="true"></i>
              {sourceWasInvoice ? "Reissue invoice" : "Turn into an invoice"}
            </h3>
            <p className="reissue-modal__subtitle">
              Same recipient and positions as
              <strong> {sourceOffer?.title || "this document"}</strong> — only the
              amounts that changed need an edit.
            </p>
          </div>
          <button
            type="button"
            className="reissue-modal__close"
            onClick={onClose}
            title="Close"
            aria-label="Close"
          >
            <i className="fas fa-xmark" aria-hidden="true"></i>
          </button>
        </header>

        <div className="reissue-modal__body">
          {isLoading ? (
            <div className="reissue-modal__loading">
              <span className="reissue-modal__spinner" aria-hidden="true"></span>
              Preparing the invoice…
            </div>
          ) : (
            <>
              <section className="reissue-modal__section">
                <div className="reissue-modal__section-head">
                  <h4 className="reissue-modal__section-title">Amounts</h4>
                  <span className="reissue-modal__section-hint">
                    Monthly positions usually stay as they are
                  </span>
                </div>

                {billableLines.length === 0 ? (
                  <p className="reissue-modal__empty">
                    This document has no billable positions left, so there is
                    nothing to invoice.
                  </p>
                ) : (
                  <ul className="reissue-modal__lines">{lines.map(renderLine)}</ul>
                )}
              </section>

              <section className="reissue-modal__section">
                <div className="reissue-modal__section-head">
                  <h4 className="reissue-modal__section-title">Recipient</h4>
                  <button
                    type="button"
                    className="reissue-modal__link-btn"
                    onClick={() => setIsEditingRecipient((editing) => !editing)}
                  >
                    {isEditingRecipient ? "Done" : "Change"}
                  </button>
                </div>

                {isEditingRecipient ? (
                  <div className="reissue-modal__grid">
                    <label className="reissue-modal__field">
                      <span className="reissue-modal__label">Name</span>
                      <input
                        type="text"
                        className="reissue-modal__input"
                        value={customerName}
                        onChange={(event) => setCustomerName(event.target.value)}
                      />
                    </label>
                    <label className="reissue-modal__field">
                      <span className="reissue-modal__label">Address</span>
                      <textarea
                        className="reissue-modal__input reissue-modal__textarea"
                        rows={3}
                        value={customerAddress}
                        onChange={(event) =>
                          setCustomerAddress(event.target.value)
                        }
                      />
                    </label>
                  </div>
                ) : (
                  <p className="reissue-modal__recipient">
                    <i className="fas fa-user" aria-hidden="true"></i>
                    <span>
                      <strong>{customerName || "No recipient set"}</strong>
                      {customerAddress && (
                        <span className="reissue-modal__recipient-address">
                          {customerAddress}
                        </span>
                      )}
                    </span>
                  </p>
                )}
              </section>

              <section className="reissue-modal__section">
                <div className="reissue-modal__section-head">
                  <h4 className="reissue-modal__section-title">Dates & number</h4>
                </div>

                <div className="reissue-modal__grid">
                  <label className="reissue-modal__field">
                    <span className="reissue-modal__label">Invoice date</span>
                    <input
                      type="date"
                      className="reissue-modal__input"
                      value={invoiceDate}
                      onChange={(event) => setInvoiceDate(event.target.value)}
                    />
                  </label>

                  <label className="reissue-modal__field">
                    <span className="reissue-modal__label">Payment due</span>
                    <input
                      type="date"
                      className="reissue-modal__input"
                      value={dueDate}
                      onChange={(event) => handleDueDateChange(event.target.value)}
                    />
                  </label>
                </div>

                <div className="reissue-modal__terms">
                  {PAYMENT_TERM_PRESETS.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      className={`reissue-modal__chip${
                        effectiveTermDays === preset
                          ? " reissue-modal__chip--active"
                          : ""
                      }`}
                      onClick={() => setTermDays(preset)}
                    >
                      {preset} days
                    </button>
                  ))}
                  <span className="reissue-modal__terms-hint">
                    {effectiveTermDays === null
                      ? "Pick a due date"
                      : sourceWasInvoice && effectiveTermDays === sourceTermDays
                        ? `Same term as ${sourceOffer?.invoiceNumber || "the original"}`
                        : `${effectiveTermDays} days from the invoice date`}
                  </span>
                </div>

                <DocumentNumberField
                  label="Invoice number"
                  value={invoiceNumber}
                  onChange={setInvoiceNumber}
                  onApplySuggestion={setInvoiceNumber}
                  analysis={numberAnalysis}
                  placeholder="e.g. RE-2026-001"
                  required
                />
              </section>
            </>
          )}
        </div>

        <footer className="reissue-modal__footer">
          <div className="reissue-modal__totals">
            {draft && (
              <>
                <span className="reissue-modal__total-label">Total</span>
                <span className="reissue-modal__total-value">
                  {formatCurrency(draft.finalTotal)}
                </span>
                {draft.taxAmount > 0 && (
                  <span className="reissue-modal__total-note">
                    incl. {formatCurrency(draft.taxAmount)} VAT
                  </span>
                )}
                {!draft.taxAmount && draft.discountedTotal !== draft.originalTotal && (
                  <span className="reissue-modal__total-note">
                    discounted from {formatCurrency(draft.originalTotal)}
                  </span>
                )}
              </>
            )}
          </div>

          <div className="reissue-modal__actions">
            {error && <span className="reissue-modal__error">{error}</span>}
            <button
              type="button"
              className="reissue-modal__cancel"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="reissue-modal__submit"
              disabled={!canSubmit}
            >
              {isSaving ? "Creating…" : "Create invoice"}
            </button>
          </div>
        </footer>
      </form>
    </div>
  );
};

export default ReissueInvoiceModal;
